import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, IconButton,
  Button, CircularProgress, Tooltip, Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { environment } from '../../Env_data/environment';
import ConfirmDialog from './ConfirmDialog';

const MCPOAuthSection = ({ userId }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [clients, setClients] = useState([]);
  const [bearerToken, setBearerToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creatingClient, setCreatingClient] = useState(false);
  const [visibleSecrets, setVisibleSecrets] = useState({});
  const [visibleBearer, setVisibleBearer] = useState(false);
  const [generatingToken, setGeneratingToken] = useState({});
  const [copiedKey, setCopiedKey] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', content: '', confirmText: 'Confirm', confirmColor: 'error', onConfirm: () => {} });
  const { enqueueSnackbar } = useSnackbar();

  const API_BASE = environment.BASE_URL ? environment.BASE_URL.replace('/api/', '') : 'http://localhost/backend/api';
  const mcpUrl = `${API_BASE}/mcp/server.php`;

  const fetchClients = async () => {
    try {
      setLoading(true);
      const [clientsRes, bearerRes] = await Promise.all([
        axios.get(`${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}`),
        axios.get(`${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}`)
      ]);
      if (clientsRes.data.success) setClients(clientsRes.data.clients);
      if (bearerRes.data.success && bearerRes.data.has_token) {
        setBearerToken({
          created_at: bearerRes.data.token_created_at,
          expires_at: bearerRes.data.token_expires_at,
          is_expired: bearerRes.data.is_expired
        });
      }
    } catch (e) {
      enqueueSnackbar('Failed to fetch credentials', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) fetchClients(); }, [userId]);

  const copyToClipboard = (text, label, key) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar(`${label} copied`, { variant: 'info' });
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCreateClient = async () => {
    try {
      setCreatingClient(true);
      const res = await axios.post(
        `${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=create`,
        { client_name: 'Spent MCP Server', redirect_uri: 'http://localhost:3000/mcp/callback' }
      );
      if (res.data.success) {
        setClients(prev => [res.data.client, ...prev]);
        enqueueSnackbar('OAuth client created', { variant: 'success' });
      }
    } catch (e) {
      enqueueSnackbar('Failed to create client', { variant: 'error' });
    } finally {
      setCreatingClient(false);
    }
  };

  const handleRegenerateSecret = async (clientId) => {
    try {
      const res = await axios.post(`${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=regenerate`, { client_id: clientId });
      if (res.data.success) {
        setClients(prev => prev.map(c => c.client_id === clientId ? { ...c, client_secret: res.data.client_secret } : c));
        enqueueSnackbar('Secret regenerated', { variant: 'success' });
      }
    } catch (e) {
      enqueueSnackbar('Failed to regenerate', { variant: 'error' });
    }
  };

  const executeDeleteClient = async (clientId) => {
    try {
      const res = await axios.post(`${API_BASE}/mcp/api/oauth_clients.php?user_id=${userId}&action=delete`, { client_id: clientId });
      if (res.data.success) {
        setClients(prev => prev.filter(c => c.client_id !== clientId));
        enqueueSnackbar('Client deleted', { variant: 'success' });
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    } catch (e) {
      enqueueSnackbar('Failed to delete', { variant: 'error' });
    }
  };

  const handleDeleteClient = (client) => {
    setConfirmDialog({
      open: true, title: 'Delete OAuth Client',
      content: `Delete "${client.client_name}"? All tokens will be revoked.`,
      confirmText: 'Delete', confirmColor: 'error',
      onConfirm: () => executeDeleteClient(client.client_id),
    });
  };

  const handleGenerateToken = async (client) => {
    try {
      setGeneratingToken(prev => ({ ...prev, [client.client_id]: true }));
      const res = await axios.post(`${API_BASE}/mcp/api/oauth_token.php?action=generate`, {
        client_id: client.client_id, client_secret: client.client_secret
      });
      if (res.data.success) {
        navigator.clipboard.writeText(res.data.access_token);
        enqueueSnackbar('Access token copied to clipboard', { variant: 'success' });
      } else {
        enqueueSnackbar(res.data.message || 'Token generation failed', { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar(e.response?.data?.message || 'Token generation failed', { variant: 'error' });
    } finally {
      setGeneratingToken(prev => ({ ...prev, [client.client_id]: false }));
    }
  };

  const handleGenerateBearerToken = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}&action=generate`);
      if (res.data.success) {
        navigator.clipboard.writeText(res.data.bearer_token);
        setBearerToken({ created_at: new Date().toISOString(), expires_at: res.data.expires_at, is_expired: false, token: res.data.bearer_token });
        enqueueSnackbar('Bearer token generated & copied', { variant: 'success' });
      }
    } catch (e) {
      enqueueSnackbar('Failed to generate bearer token', { variant: 'error' });
    } finally { setLoading(false); }
  };

  const executeRevokeBearerToken = async () => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/mcp/api/bearer_token.php?user_id=${userId}&action=revoke`);
      if (res.data.success) {
        setBearerToken(null);
        enqueueSnackbar('Bearer token revoked', { variant: 'success' });
        setConfirmDialog(prev => ({ ...prev, open: false }));
      }
    } catch (e) { enqueueSnackbar('Failed to revoke', { variant: 'error' }); }
    finally { setLoading(false); }
  };

  // Shared field styles
  const fieldBg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const fieldBorder = `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`;

  // Compact copy icon
  const CopyIcon = ({ text, label, fieldKey }) => (
    <IconButton size="small" onClick={() => copyToClipboard(text, label, fieldKey)} sx={{ p: 0.4 }}>
      {copiedKey === fieldKey
        ? <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
        : <ContentCopyIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
    </IconButton>
  );

  return (
    <Box sx={{
      mb: 1.5, overflow: 'hidden', borderRadius: 3, border: 'none',
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.015)',
      boxShadow: isDark
        ? `0 1px 3px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)`
        : `0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)`,
    }}>
      {/* ── Header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}` }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'text.primary', letterSpacing: -0.2 }} noWrap>Spent MCP Server</Typography>
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }} noWrap>Connect Claude, ChatGPT or any MCP client</Typography>
        </Box>
        <Button size="small" variant="contained" disableElevation
          startIcon={creatingClient ? <CircularProgress size={12} color="inherit" /> : <AddIcon />}
          onClick={handleCreateClient} disabled={creatingClient || loading}
          sx={{ fontSize: 11, whiteSpace: 'nowrap', ml: 1, flexShrink: 0, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
          New Client
        </Button>
      </Stack>

      <Box sx={{ p: 2 }}>
        {/* ── Endpoint ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, p: 1, borderRadius: 1, bgcolor: fieldBg, border: fieldBorder, mb: 1.5 }}>
          <LinkIcon sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.primary', overflowX: 'auto', whiteSpace: 'nowrap', flex: 1, '&::-webkit-scrollbar': { height: 0 } }}>
            {mcpUrl}
          </Typography>
          <CopyIcon text={mcpUrl} label="Endpoint" fieldKey="endpoint" />
        </Box>

        {/* ── Clients ── */}
        {loading ? (
          <Box textAlign="center" py={3}><CircularProgress size={20} /></Box>
        ) : clients.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No clients yet — click <b>New Client</b> to get started.
          </Typography>
        ) : (
          <Stack gap={1} mb={1.5}>
            {clients.map((client) => (
              <Box key={client.client_id} sx={{ border: fieldBorder, borderRadius: 1.5, overflow: 'hidden' }}>

                {/* name row */}
                <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.75, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)' }}>
                  <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'success.main', mr: 1, flexShrink: 0 }} />
                  <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }} noWrap>{client.client_name}</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5, fontSize: 10, flexShrink: 0 }}>
                    {client.created_at ? new Date(client.created_at).toLocaleDateString() : ''}
                  </Typography>
                  <IconButton size="small" onClick={() => handleDeleteClient(client)} sx={{ p: 0.3, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Stack>

                <Box sx={{ px: 1.5, py: 1 }}>
                  {/* Client ID */}
                  <Stack direction="row" alignItems="center" gap={0.75} mb={0.5}>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', width: 44, flexShrink: 0 }}>Client ID</Typography>
                    <Box sx={{ flex: 1, minWidth: 0, px: 1, py: 0.5, borderRadius: 0.75, bgcolor: fieldBg, border: fieldBorder, overflowX: 'auto', '&::-webkit-scrollbar': { height: 0 } }}>
                      <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.primary', whiteSpace: 'nowrap' }}>{client.client_id}</Typography>
                    </Box>
                    <CopyIcon text={client.client_id} label="Client ID" fieldKey={`cid-${client.client_id}`} />
                  </Stack>

                  {/* Secret */}
                  <Stack direction="row" alignItems="center" gap={0.75} mb={0.75}>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary', width: 44, flexShrink: 0 }}>Secret</Typography>
                    <Box sx={{ flex: 1, minWidth: 0, px: 1, py: 0.5, borderRadius: 0.75, bgcolor: fieldBg, border: fieldBorder, overflow: 'hidden' }}>
                      <Typography sx={{ fontSize: 10, fontFamily: 'monospace', color: 'text.primary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {visibleSecrets[client.client_id] ? client.client_secret : '••••••••••••••••••••'}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setVisibleSecrets(p => ({ ...p, [client.client_id]: !p[client.client_id] }))} sx={{ p: 0.4 }}>
                      {visibleSecrets[client.client_id] ? <VisibilityOffIcon sx={{ fontSize: 14, opacity: 0.5 }} /> : <VisibilityIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
                    </IconButton>
                    <CopyIcon text={client.client_secret} label="Secret" fieldKey={`sec-${client.client_id}`} />
                  </Stack>

                  {/* Actions */}
                  <Stack direction="row" gap={0.75}>
                    <Button fullWidth size="small" variant="outlined" color="success"
                      startIcon={generatingToken[client.client_id] ? <CircularProgress size={10} /> : <KeyIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleGenerateToken(client)}
                      disabled={!!generatingToken[client.client_id]}
                      sx={{ fontSize: 10, py: 0.5, textTransform: 'none', fontWeight: 600 }}>
                      {generatingToken[client.client_id] ? 'Generating…' : 'Get Access Token'}
                    </Button>
                    <Tooltip title="Regenerate secret" arrow>
                      <IconButton size="small" onClick={() => handleRegenerateSecret(client.client_id)}
                        sx={{ border: fieldBorder, borderRadius: 1, px: 1 }}>
                        <RefreshIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {/* ── Bearer Token ── */}
        <Divider sx={{ mb: 1.5 }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.75}>
          <Box>
            <Typography variant="caption" fontWeight={700}>Bearer Token</Typography>
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Long-lived · 1 year</Typography>
          </Box>
          {bearerToken && !bearerToken.is_expired ? (
            <Button size="small" color="error" variant="text" sx={{ fontSize: 10, fontWeight: 700, minWidth: 'auto' }}
              onClick={() => setConfirmDialog({ open: true, title: 'Revoke Bearer Token', content: 'Connected clients will lose access.', confirmText: 'Revoke', confirmColor: 'error', onConfirm: executeRevokeBearerToken })}
              disabled={loading}>
              Revoke
            </Button>
          ) : (
            <Button size="small" variant="contained" color="success" disableElevation
              startIcon={loading ? <CircularProgress size={10} color="inherit" /> : <KeyIcon sx={{ fontSize: 13 }} />}
              onClick={handleGenerateBearerToken} disabled={loading}
              sx={{ fontSize: 10, textTransform: 'none' }}>
              Generate
            </Button>
          )}
        </Stack>

        {bearerToken && !bearerToken.is_expired ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1, borderRadius: 1, bgcolor: fieldBg, border: fieldBorder }}>
              <Typography sx={{ flex: 1, fontSize: 10, fontFamily: 'monospace', color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {visibleBearer ? (bearerToken.token || '••••••••') : '••••••••••••••••••••••••••••'}
              </Typography>
              <IconButton size="small" onClick={() => setVisibleBearer(v => !v)} sx={{ p: 0.4 }}>
                {visibleBearer ? <VisibilityOffIcon sx={{ fontSize: 14, opacity: 0.5 }} /> : <VisibilityIcon sx={{ fontSize: 14, opacity: 0.5 }} />}
              </IconButton>
              {bearerToken.token && <CopyIcon text={bearerToken.token} label="Bearer" fieldKey="bearer" />}
            </Box>
            <Typography sx={{ fontSize: 9, color: 'text.disabled', mt: 0.5 }}>
              Created {new Date(bearerToken.created_at).toLocaleDateString()} · Expires {new Date(bearerToken.expires_at).toLocaleDateString()}
            </Typography>
          </Box>
        ) : !loading && (
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>No token — generate one for long-lived auth.</Typography>
        )}
      </Box>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        title={confirmDialog.title} content={confirmDialog.content}
        confirmText={confirmDialog.confirmText} confirmColor={confirmDialog.confirmColor}
        onConfirm={confirmDialog.onConfirm}
      />
    </Box>
  );
};

export default MCPOAuthSection;
